import { Steps } from 'antd';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useProgramStepContext } from '../contexts/ProgramStepContext';
import { ProgramStatus } from '../types/program';
import { showInfoModal } from '../utils/modalHelpers';
import { verifyCompletedConfigurationStep } from '../utils/steps/ConfigurationStep/verifyCompletedConfigurationStep';
import { verifyCompletedContentStep } from '../utils/steps/ContentStep/verifyCompletedContentStep';
import { verifyCompletedInitialStep } from '../utils/steps/initialStep/verifyCompletedInitialStep';
import { verifyCompletedVisualStep } from '../utils/steps/VisualStep/verifyCompletedVisualStep';

export interface ProgramStepperProps {
  programId?: number;
  currentStep: number;
  onChangeStep: (value: number) => void;
}

const StepNamesMap = {
  0: 'Inicio',
  1: 'Configurações',
  2: 'Conteúdos',
  3: 'Visual',
  4: 'Publicação',
};

export const ProgramStepper: FC<ProgramStepperProps> = ({
  programId,
  currentStep,
  onChangeStep,
}) => {
  const { program, setShouldShowStepErrors, isPublishedProgram } =
    useProgramStepContext();
  const [stepsCompleted, setStepsCompleted] = useState<Record<number, boolean>>(
    {
      0: false,
      1: false,
      2: false,
      3: false,
      4: false,
    }
  );

  useEffect(() => {
    const checkStepsCompletion = async () => {
      const initialStepComplete = await verifyCompletedInitialStep(program);
      const configurationStepComplete = await verifyCompletedConfigurationStep(
        program
      );
      const contentStepComplete = await verifyCompletedContentStep(program);
      const visualStepComplete = await verifyCompletedVisualStep(program);

      setStepsCompleted({
        0: initialStepComplete,
        1: configurationStepComplete,
        2: contentStepComplete,
        3: visualStepComplete,
        4:
          program?.status === ProgramStatus.Scheduled ||
          program?.status === ProgramStatus.Active,
      });
    };

    checkStepsCompletion();
  }, [program]);

  const handleChange = (value: number) => {
    if (value > 0 && !programId) {
      return showInfoModal({
        title: 'Crie o programa antes de avançar',
        content:
          'Antes de prosseguir, é necessário criar o programa. Preencha as informações iniciais e salve para desbloquear as próximas etapas.',
      });
    }

    setShouldShowStepErrors?.(true);

    if (programId) {
      for (let i = 0; i < value; i++) {
        if (!stepsCompleted[i]) {
          return showInfoModal({
            title: 'Finalize as etapas necessárias antes de prosseguir',
            content: `Para avançar para a etapa "${
              StepNamesMap[value as keyof typeof StepNamesMap]
            }", é necessário preencher todas as informações obrigatórias das etapas anteriores que ainda não foram concluídas. Revise os dados das etapas pendentes e salve antes de continuar.`,
          });
        }
      }
    }

    if (!isPublishedProgram) {
      setShouldShowStepErrors?.(false);
    }

    onChangeStep(value);
  };

  return (
    <StyledSteps
      type='navigation'
      current={currentStep}
      onChange={handleChange}
      labelPlacement='horizontal'
      size='default'
      items={steps.map((title, index) => {
        const isCompleted = stepsCompleted[index];
        const isCurrent = currentStep === index;
        return {
          title,
          status: (isCompleted ? 'finish' : isCurrent ? 'process' : 'wait') as
            | 'wait'
            | 'process'
            | 'finish'
            | 'error'
            | undefined,
        };
      })}
    />
  );
};

const steps = ['Inicio', 'Configurações', 'Conteúdos', 'Visual', 'Publicação'];

export const StyledSteps = styled(Steps)`
  margin-bottom: 40px;
  box-shadow: 0px -1px 0 0 #383838 inset;
`;
